/**
 * Flower nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const flowerNailDesignsPack: ToilePack = {
    slug: 'flower-nail-designs',
    category: 'beauty',
    name: 'Flower nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'daisy', label: 'Daisies', subject: 'short round nails in a milky base with small white daisies with yellow centres on every nail'},
        {key: 'rose', label: 'Roses', subject: 'almond nails in nude with one painted red rose and green leaves on two accent nails'},
        {key: 'pressed', label: 'Pressed flowers', subject: 'oval nails with real tiny dried flowers in pink and purple set flat under a clear gel layer'},
    ],
    styles: [
        {key: 'salon', label: 'Salon close-up', style: 'nail salon close-up in soft diffuse daylight from camera-left, a gentle shadow under the fingers, a bright airy balance', palette: 'Clean white ground, the flower colors carried on the nails', framing: 'the hand resting flat on a white tabletop, fingertips toward the top of a vertical frame'},
        {key: 'garden', label: 'Garden bunch', style: 'outdoor lifestyle photo in soft open daylight from above, dappled leaf shadow, a fresh green mid-tone balance', palette: 'Soft greens and meadow flowers behind, the painted petals crisp on the nails', framing: 'the hand holding a small loose bunch of fresh wildflowers in a garden, the background softly out of focus'},
        {key: 'linen', label: 'Pink linen', style: 'beauty editorial in warm light from camera-right, soft shadow on one side, a gentle pastel balance', palette: 'Blush pink linen all around, the flowers bright against it', framing: 'the hand laid on soft blush pink linen filling the frame, one dried stem lying beside it'},
    ],
    directions: ['sunflowers', 'spring pastels', 'cherry blossom'],
    directionHint: 'try: sunflowers',
    seo: {
        title: 'Flower Nail Designs: Daisies, Roses, Pressed Flowers',
        metaDescription: 'Nine flower nail designs: tiny white daisies, a painted red rose and dried flowers pressed under gel, in three settings.',
        h1: 'Flower nail designs: daisies, roses and pressed flowers under gel',
        tagline: 'Nine floral sets: three designs, each shown in a salon close-up, in a garden bunch and on pink linen.',
        intro: 'Flower nail designs for people who want flowers painted on their nails, small and sweet or bold. The three designs are short round nails in a milky base with small white daisies, almond nails in nude with one painted red rose and green leaves, and oval nails with tiny dried flowers in pink and purple set under a clear gel layer. Type a different flower and the same floral idea carries it.',
        useCases: [
            'a daisy set for a spring party',
            'a rose on nude for a wedding',
            'pressed flowers for a keepsake look',
            'a small flower on one nail for work',
        ],
        faq: [
            {q: 'What are the pressed flowers?', a: 'Tiny dried flowers in pink and purple, set flat under a clear gel layer on oval nails.'},
            {q: 'How many nails carry a flower?', a: 'The daisy set has a daisy on every nail; the rose set has one painted rose on two accent nails.'},
            {q: 'Can I ask for a different flower?', a: 'Yes. A flower you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
